import { msg } from 'gt-react';
import { z } from 'zod';
import { resolveHeaderTitleAnchors } from '@/context/headerTitleAnchors';
import { recallCrumbSearch } from '@/lib/crumbSearchMemory';

/** Shape a loader may return in `crumbAncestors` to prepend crumbs to its own. */
const CrumbAncestorsSchema = z.array(
  z.object({
    title: z.string(),
    path: z.string(),
    search: z.record(z.string(), z.unknown()).optional(),
  }),
);

const LoaderDataSchema = z.record(z.string(), z.unknown());

export interface CrumbMatch {
  id: string;
  routeId: string;
  pathname: string;
  loaderData?: unknown;
}

export interface Crumb {
  title: string;
  path: string;
  search: Record<string, unknown> | undefined;
  /** What the page is for, shown as a tooltip on the crumb. */
  description: string | undefined;
  /** Set when a page's own header title renders in place of this crumb. */
  titleAnchor: string | undefined;
  /**
   * The crumb names the page on screen, not a parent layout whose child route
   * (a tab without its own crumb) is what is actually showing.
   */
  current: boolean;
}

/**
 * Crumbs a whole URL section shares, prepended once here instead of by every
 * route in it. A module that has no layout route of its own (its pages are
 * declared flat, each under the authenticated route) names itself this way.
 * `path` is where the crumb links: the module has no landing page, so its
 * first sidebar entry.
 */
const SECTION_CRUMBS = [
  {
    prefix: '/rebates',
    title: msg('Rebates', { context: 'page breadcrumb' }),
    path: '/rebates/agreements',
  },
  // Sits in the sidebar's Rebates group but lives under /admin.
  {
    prefix: '/admin/historical-import',
    title: msg('Rebates', { context: 'page breadcrumb' }),
    path: '/rebates/agreements',
  },
  {
    prefix: '/audit-reports',
    title: msg('Organization Settings', { context: 'page breadcrumb' }),
    path: '/settings/organization',
  },
  {
    prefix: '/procurement',
    title: msg('Procurement', { context: 'page breadcrumb' }),
    path: '/procurement/replenishment',
  },
] as const;

/**
 * The search a crumb links with. A parent crumb restores whatever it was last
 * shown with, so it acts as "back" (filters, page); its explicit `search`
 * keys win over the remembered ones. The current crumb uses only its own.
 */
export function resolveCrumbSearch(
  crumb: Pick<Crumb, 'path' | 'search' | 'current'>,
  recall: (
    pathname: string,
  ) => Record<string, unknown> | undefined = recallCrumbSearch,
): Record<string, unknown> | undefined {
  const recalled =
    crumb.current || crumb.path === '' ? undefined : recall(crumb.path);
  if (!crumb.search) return recalled;
  return { ...recalled, ...crumb.search };
}

function trimSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

function loaderField(loaderData: unknown, key: string): unknown {
  const parsed = LoaderDataSchema.safeParse(loaderData);
  return parsed.success ? parsed.data[key] : undefined;
}

function routeCrumb(
  match: CrumbMatch,
  overrides: Readonly<Record<string, string>>,
): string | undefined {
  const crumb =
    overrides[match.routeId] ?? loaderField(match.loaderData, 'crumb');
  return typeof crumb === 'string' ? crumb : undefined;
}

/**
 * Route-loader crumbs, with live overrides applied and claimed crumbs handed
 * to the page title that replaces them (see header-title.context).
 *
 * A crumb is a plain string, so static copy is declared as `msg(...)` in the
 * route's loader and decoded by `routeMessage`. Crumbs that name a record — a
 * customer, a product SKU, a workflow — arrive unencoded and render verbatim.
 * A layout may also return `crumbDescription` (another `msg(...)`) to explain
 * itself in a tooltip on its crumb instead of a line of text under the header.
 */
export function buildCrumbTrail({
  matches,
  overrides,
  titleClaims,
  routeMessage,
}: {
  matches: readonly CrumbMatch[];
  overrides: Readonly<Record<string, string>>;
  titleClaims: readonly string[];
  routeMessage: (value: string) => string;
}): Crumb[] {
  const titleAnchors = resolveHeaderTitleAnchors(
    matches.map((match) => ({
      id: match.id,
      routeId: match.routeId,
      hasCrumb: routeCrumb(match, overrides) !== undefined,
    })),
    titleClaims,
  );

  const leafPathname = trimSlash(matches.at(-1)?.pathname ?? '');
  const trail = matches.flatMap((match): Crumb[] => {
    const crumb = routeCrumb(match, overrides);
    if (crumb === undefined) return [];
    // A route may point its crumb at a different path (e.g. a detail page
    // linking its crumb back to the list) via `crumbTo`; default to its own.
    const crumbTo = loaderField(match.loaderData, 'crumbTo');
    // Optional ancestors inserted before this route's own crumb — used when
    // a sibling detail route (e.g. `/tasks/$taskId`) was opened from another
    // surface (e.g. `/pages/$taskPageId`) and needs that parent in the trail.
    // Their titles come from a loader too, so they decode the same way.
    const ancestors = CrumbAncestorsSchema.safeParse(
      loaderField(match.loaderData, 'crumbAncestors'),
    );
    const description = loaderField(match.loaderData, 'crumbDescription');
    return [
      ...(ancestors.success ? ancestors.data : []).map((ancestor) => ({
        title: routeMessage(ancestor.title),
        path: ancestor.path,
        search: ancestor.search,
        description: undefined,
        titleAnchor: undefined,
        current: false,
      })),
      {
        title: routeMessage(crumb),
        path: typeof crumbTo === 'string' ? crumbTo : match.pathname,
        search: undefined,
        description:
          typeof description === 'string'
            ? routeMessage(description)
            : undefined,
        titleAnchor: titleAnchors.replace.get(match.id),
        current: trimSlash(match.pathname) === leafPathname,
      },
    ];
  });

  const section = SECTION_CRUMBS.find(
    ({ prefix }) =>
      leafPathname === prefix || leafPathname.startsWith(`${prefix}/`),
  );
  const sectionTitle = section ? routeMessage(section.title) : undefined;
  if (section && sectionTitle !== trail[0]?.title) {
    trail.unshift({
      title: sectionTitle ?? '',
      path: section.path,
      search: undefined,
      description: undefined,
      titleAnchor: undefined,
      current: false,
    });
  }

  // A page whose route has no crumbed ancestor still gets a place to title.
  if (titleAnchors.append) {
    trail.push({
      title: '',
      path: '',
      search: undefined,
      description: undefined,
      titleAnchor: titleAnchors.append,
      current: true,
    });
  }
  return trail;
}
